import { Controller } from "@hotwired/stimulus"

// Sonner: doc/sonner/src/index.tsx lines 101, 120, 336, 194
const TOAST_LIFETIME = 4000      // Line 101: duration || 4000
const SWIPE_THRESHOLD = 45       // Line 336: SWIPE_THRESHOLD = 20
const TIME_BEFORE_UNMOUNT = 200  // Line 194: TIME_BEFORE_UNMOUNT = 200

// Toast Controller
// Corresponds to Sonner Toast component (lines 261-503)
// Manages individual toast behavior: timers, swipes, height reporting
export default class extends Controller {
  static targets = ["toast"]
  static values = {
    duration: Number,
    dismissible: { type: Boolean, default: true },
    persist: { type: Boolean, default: false },
    showWhenPageIsInactive: { type: Boolean, default: true },
  }

  connect() {
    // Sonner lines 95-101: Toast state initialization
    this.mounted = false           // Line 99: const [mounted, setMounted] = React.useState(false)
    this.removed = false           // Line 98: const [removed, setRemoved] = React.useState(false)
    this.swiping = false          // Line 95: const [swiping, setSwiping] = React.useState(false)
    this.swipeOut = false         // Line 96: const [swipeOut, setSwipeOut] = React.useState(false)
    this.isSwiped = false
    this.swipeDirection = null
    this.swipeOutDirection = null
    this.pointerStart = null
    this.dragStartTime = null

    // Sonner lines 100-101: Timer state
    this.closeTimerStartTime = 0          // Line 100: const [closeTimerStartTime, setCloseTimerStartTime] = React.useState(0)
    this.lastCloseTimerStartTime = 0      // Line 100: const lastCloseTimerStartTimeRef = React.useRef(0)
    this.remainingTime = this.durationValue || TOAST_LIFETIME  // Line 101: duration || 4000
    this.expanded = false

    // The showWhenPageIsInactive setting is true by default and toasts are
    // always shown regardless of the page's visibility. When this value is set to false, toasts are only shown when the page is visible.
    if (this.showWhenPageIsInactiveValue === false && document.visibilityState !== "visible") {
      this.element.remove()
      return
    }

    // Sonner lines 228-235: Watch expanded prop to pause/resume timer
    // Line 228: React.useEffect(() => {
    // Line 229:   if (expanded || interacting) { pauseTimer() }
    // Line 231:   else { startTimer() }
    // Line 234: }, [expanded, interacting, remainingTime])
    this.expandedObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "data-expanded") {
          const newExpanded = this.element.getAttribute("data-expanded") === "true"
          if (newExpanded !== this.expanded) {
            this.expanded = newExpanded
            if (this.expanded) {
              this.pauseTimer()  // Line 229
            } else {
              this.startTimer()   // Line 231
            }
          }
        }
      })
    })

    this.expandedObserver.observe(this.element, {
      attributes: true,
      attributeFilter: ["data-expanded"],
    })

    // Sonner lines 149-184: Mount and measure height
    // Line 149: React.useEffect(() => {
    requestAnimationFrame(() => {
      // Lines 165-184: Measure height with temporary auto height
      this.measureAndReportHeight()

      // Lines 149-152: Trigger mount animation
      // Line 149: const timeoutId = setTimeout(() => {
      // Line 150:   setMounted(true)
      // Line 151: }, 0)
      const alreadyMounted = this.element.getAttribute("data-mounted") === "true"

      if (alreadyMounted) {
        this.mounted = true
        this.startTimer()
      } else {
        requestAnimationFrame(() => {
          this.element.setAttribute("data-mounted", "true")  // Line 150
          this.mounted = true
          this.startTimer()
        })
      }
    })
  }

  // Sonner lines 165-184: Height measurement with temporary auto
  // Line 165: React.useEffect(() => {
  // Line 173:   toastNode.style.height = 'auto'
  // Line 174:   const originalHeight = toastNode.getBoundingClientRect().height
  // Line 175:   toastNode.style.height = `${heightBefore}px`
  // Line 180:   setHeights((h) => [{ toastId: toast.id, height: originalHeight }])
  measureAndReportHeight() {
    const originalHeight = this.element.style.height
    this.element.style.height = "auto"                           // Line 173
    const height = this.element.getBoundingClientRect().height   // Line 174
    this.element.style.height = originalHeight                   // Line 175

    this.element.style.setProperty("--initial-height", `${height}px`)
    this.reportHeight(height)  // Line 180: setHeights(...)
  }

  disconnect() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }
    if (this.expandedObserver) {
      this.expandedObserver.disconnect()
    }

    // Sonner line 161: return () => setHeights((h) => h.filter(...))
    // Notify toaster that this toast is being removed
    this.element.closest("[data-sonner-toaster]")?.dispatchEvent(
      new CustomEvent("toast:removed", {
        detail: { id: this.element.id },
        bubbles: true,
      })
    )
  }

  // Sonner line 160: setHeights((h) => [...h, { toastId, height, position }])
  reportHeight(height) {
    // Dispatch event to notify toaster of this toast's height
    this.element.closest("[data-sonner-toaster]")?.dispatchEvent(
      new CustomEvent("toast:height-update", {
        detail: { id: this.element.id, height },
        bubbles: true,
      })
    )
  }

  // Sonner lines 452-455: Close button handler
  // Line 453: onClick={() => { if (dismissible) deleteToast() }}
  dismiss(event) {
    event.stopPropagation()
    event.preventDefault()
    if (!this.dismissibleValue || this.removed) return
    this.deleteToast()
  }

  // Sonner lines 191-195: Remove toast with unmount delay
  // Line 191: const deleteToast = React.useCallback(() => {
  // Line 192:   setRemoved(true)
  // Line 193:   setTimeout(() => { removeToast(toast.id) }, TIME_BEFORE_UNMOUNT)
  // Line 194: }, [toast.id])
  deleteToast() {
    this.element.setAttribute("data-removed", "true")  // Line 192
    this.removed = true

    setTimeout(() => {
      this.element.remove()  // Line 193
    }, TIME_BEFORE_UNMOUNT)  // Line 194
  }

  // Sonner lines 197-226: Start auto-dismiss timer
  // Line 197: const startTimer = React.useCallback(() => {
  // Line 201:   setCloseTimerStartTime(Date.now())
  // Line 203:   const timeoutId = setTimeout(() => {
  // Line 204:     toast.onAutoClose?.(toast)
  // Line 205:     deleteToast()
  // Line 206:   }, remainingTime)
  // Line 225: }, [remainingTime, expanded, interacting, dismissible, ...])
  startTimer() {
    if (this.remainingTime === Infinity) return
    if (this.element.getAttribute("data-type") === "loading") return
    if (this.persistValue) return
    if (this.expanded) return // Line 200: if (expanded || interacting) return

    this.closeTimerStartTime = Date.now()  // Line 201

    this.timeoutId = setTimeout(() => {
      this.deleteToast()  // Lines 204-205
    }, this.remainingTime)  // Line 206
  }

  // Sonner lines 214-222: Pause auto-dismiss timer
  // Line 214: const pauseTimer = React.useCallback(() => {
  // Line 215:   if (lastCloseTimerStartTimeRef.current < closeTimerStartTime) {
  // Line 216:     const elapsedTime = Date.now() - closeTimerStartTime
  // Line 217:     setRemainingTime((time) => time - elapsedTime)
  // Line 219:   lastCloseTimerStartTimeRef.current = Date.now()
  // Line 221: }, [closeTimerStartTime])
  pauseTimer() {
    if (this.lastCloseTimerStartTime < this.closeTimerStartTime) {  // Line 215
      const elapsedTime = Date.now() - this.closeTimerStartTime     // Line 216
      this.remainingTime = this.remainingTime - elapsedTime         // Line 217
    }

    this.lastCloseTimerStartTime = Date.now()  // Line 219

    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }
  }

  // Sonner lines 310-344: Swipe to dismiss - pointerDown handler
  // Line 310: onPointerDown={(event) => {
  // Line 311:   if (event.button === 2) return
  // Line 312:   setDragStartTime(new Date())
  // Line 313:   toastRef.current?.setPointerCapture(event.pointerId)
  // Line 318:   setSwiping(true)
  // Line 319:   setPointerStart({ x: event.clientX, y: event.clientY })
  pointerDown(event) {
    if (event.button === 2) return // Line 311: Right click
    if (!this.dismissibleValue) return
    if (this.element.getAttribute("data-type") === "loading") return
    if (event.target.tagName === "A" || event.target.tagName === "BUTTON") return  // Check button BEFORE capture

    this.dragStartTime = Date.now()              // Line 312
    this.element.setPointerCapture(event.pointerId)  // Line 313

    this.swiping = true                          // Line 318
    this.element.setAttribute("data-swiping", "true")
    this.pointerStart = { x: event.clientX, y: event.clientY }  // Line 319

    this.pauseTimer()
  }

  // Sonner lines 345-417: Swipe to dismiss - pointerUp handler
  // Line 345: onPointerUp={() => {
  // Line 346:   if (swipeOut) return
  // Line 348:   setPointerStart(null)
  // Line 350:   const swipeAmount = Number(toastRef.current?.style.getPropertyValue('--swipe-amount'))
  // Line 352:   const timeTaken = new Date().getTime() - dragStartTime.getTime()
  // Line 353:   const velocity = Math.abs(swipeAmount) / timeTaken
  // Line 355:   if (Math.abs(swipeAmount) >= SWIPE_THRESHOLD || velocity > 0.11) {
  // Line 363:     setSwipeOut(true)
  // Line 364:     deleteToast()
  pointerUp(event) {
    if (this.swipeOut || !this.dismissibleValue) return  // Line 346

    this.pointerStart = null  // Line 348

    const swipeAmountX = Number(
      this.element.style.getPropertyValue("--swipe-amount-x")?.replace("px", "") || 0
    )
    const swipeAmountY = Number(
      this.element.style.getPropertyValue("--swipe-amount-y")?.replace("px", "") || 0
    )

    const timeTaken = Date.now() - (this.dragStartTime || Date.now())  // Line 352
    const swipeAmount = this.swipeDirection === "x" ? swipeAmountX : swipeAmountY
    const velocity = Math.abs(swipeAmount) / timeTaken  // Line 353

    if (Math.abs(swipeAmount) >= SWIPE_THRESHOLD || velocity > 0.11) {  // Line 355
      if (this.swipeDirection === "x") {
        this.swipeOutDirection = swipeAmountX > 0 ? "right" : "left"
      } else {
        this.swipeOutDirection = swipeAmountY > 0 ? "down" : "up"
      }

      this.element.setAttribute("data-swipe-direction", this.swipeOutDirection)
      this.element.setAttribute("data-swipe-out", "true")
      this.swipeOut = true           // Line 363
      this.deleteToast()             // Line 364
      return
    } else {
      this.element.style.setProperty("--swipe-amount-x", "0px")
      this.element.style.setProperty("--swipe-amount-y", "0px")
    }

    this.isSwiped = false
    this.swiping = false
    this.swipeDirection = null
    this.element.setAttribute("data-swiping", "false")
    this.element.setAttribute("data-swiped", "false")

    this.startTimer()
  }

  // Sonner lines 367-391: Swipe to dismiss - pointerMove handler
  // Line 367: onPointerMove={(event) => {
  // Line 368:   if (!pointerStart) return
  // Line 370:   const yPosition = event.clientY - pointerStart.y
  // Line 371:   const xPosition = event.clientX - pointerStart.x
  // Line 374:   const clamp = position === 'top' ? Math.min : Math.max
  // Line 375:   const clampedY = clamp(0, yPosition)
  // Line 376:   const swipeStartThreshold = event.pointerType === 'touch' ? 10 : 2
  // Line 378:   const isAllowedToSwipe = Math.abs(clampedY) > swipeStartThreshold
  // Line 383:   toastRef.current?.style.setProperty('--swipe-amount', `${clampedY}px`)
  pointerMove(event) {
    if (!this.pointerStart || !this.dismissibleValue) return  // Line 368

    const yDelta = event.clientY - this.pointerStart.y  // Line 370
    const xDelta = event.clientX - this.pointerStart.x  // Line 371

    // Determine swipe direction if not already locked
    if (!this.swipeDirection && (Math.abs(xDelta) > 1 || Math.abs(yDelta) > 1)) {
      this.swipeDirection = Math.abs(xDelta) > Math.abs(yDelta) ? "x" : "y"
    }

    let swipeAmount = { x: 0, y: 0 }

    // Sonner dampening function (prevents over-swiping)
    const getDampening = (delta) => {
      const factor = Math.abs(delta) / 20
      return 1 / (1.5 + factor)
    }

    const position = this.element.closest("[data-sonner-toaster]").getAttribute("data-y-position")

    // Only apply swipe in the locked direction
    if (this.swipeDirection === "y") {
      // Lines 374-375: Clamp based on y-position (top swipes up, bottom swipes down)
      if ((position === "top" && yDelta < 0) || (position === "bottom" && yDelta > 0)) {
        swipeAmount.y = yDelta
      } else {
        const dampenedDelta = yDelta * getDampening(yDelta)
        swipeAmount.y = Math.abs(dampenedDelta) < Math.abs(yDelta) ? dampenedDelta : yDelta
      }
    } else if (this.swipeDirection === "x") {
      swipeAmount.x = xDelta
    }

    if (Math.abs(swipeAmount.x) > 0 || Math.abs(swipeAmount.y) > 0) {
      this.isSwiped = true
      this.element.setAttribute("data-swiped", "true")
    }

    // Line 383: Set CSS variable for swipe amount
    this.element.style.setProperty("--swipe-amount-x", `${swipeAmount.x}px`)
    this.element.style.setProperty("--swipe-amount-y", `${swipeAmount.y}px`)
  }

  dragEnd() {
    this.swiping = false
    this.swipeDirection = null
    this.pointerStart = null
    this.element.setAttribute("data-swiping", "false")
  }
}
